import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserX, UserPlus, Trash2, Ban } from "lucide-react";

interface BannedUser {
  id: string;
  user_id?: string;
  email?: string;
  reason?: string;
  banned_by?: string;
  banned_at: string;
  created_at: string;
}

interface BanUserForm {
  email: string;
  reason: string;
}

const BannedUsersManager = () => {
  const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [banForm, setBanForm] = useState<BanUserForm>({ email: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchBannedUsers();
  }, []);

  const fetchBannedUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('banned_users')
        .select('*')
        .order('banned_at', { ascending: false });

      if (error) throw error;
      setBannedUsers(data || []);
    } catch (error) {
      console.error('Error fetching banned users:', error);
      toast({
        title: "Error",
        description: "Failed to load banned users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const banUser = async () => {
    if (!banForm.email.trim()) {
      toast({
        title: "Error",
        description: "Email is required",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      // Check if user is already banned
      const { data: existing, error: checkError } = await supabase
        .from('banned_users')
        .select('id')
        .eq('email', banForm.email.trim().toLowerCase())
        .maybeSingle();

      if (checkError) throw checkError;

      if (existing) {
        toast({
          title: "Error",
          description: "User is already banned",
          variant: "destructive"
        });
        return;
      }

      // Get current user ID for banned_by field
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Add to banned users
      const { error } = await supabase
        .from('banned_users')
        .insert({
          email: banForm.email.trim().toLowerCase(),
          reason: banForm.reason.trim() || null,
          banned_by: user?.id
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User has been banned successfully"
      });

      setBanForm({ email: '', reason: '' });
      setIsAddDialogOpen(false);
      fetchBannedUsers();
    } catch (error) {
      console.error('Error banning user:', error);
      toast({
        title: "Error",
        description: "Failed to ban user",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const unbanUser = async (bannedUserId: string) => {
    if (!confirm('Are you sure you want to unban this user?')) return;

    try {
      const { error } = await supabase
        .from('banned_users')
        .delete()
        .eq('id', bannedUserId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "User has been unbanned successfully"
      });

      fetchBannedUsers();
    } catch (error) {
      console.error('Error unbanning user:', error);
      toast({
        title: "Error",
        description: "Failed to unban user",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-32">Loading banned users...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Banned Users Management
            </CardTitle>
            <CardDescription>
              Manage user access restrictions and view banned user list
            </CardDescription>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <UserX className="w-4 h-4" />
                Ban User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Ban User</DialogTitle>
                <DialogDescription>
                  Add a user to the banned list. They will not be able to access the system.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={banForm.email}
                    onChange={(e) => setBanForm({ ...banForm, email: e.target.value })}
                    disabled={submitting}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="reason">Reason (Optional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Enter reason for banning this user..."
                    value={banForm.reason}
                    onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
                    disabled={submitting}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button 
                  type="submit" 
                  onClick={banUser}
                  disabled={submitting || !banForm.email.trim()}
                >
                  {submitting ? "Banning..." : "Ban User"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {bannedUsers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No Banned Users</p>
            <p>No users are currently banned from the system.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Badge variant="destructive" className="text-sm">
                {bannedUsers.length} banned user{bannedUsers.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Banned Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bannedUsers.map((bannedUser) => (
                  <TableRow key={bannedUser.id}>
                    <TableCell className="font-medium">
                      {bannedUser.email || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {bannedUser.reason ? (
                        <span className="text-sm">{bannedUser.reason}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">No reason provided</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(bannedUser.banned_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unbanUser(bannedUser.id)}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="w-3 h-3" />
                        Unban
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BannedUsersManager;